import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  initAnalytics,
  identifyUser,
  resetAnalytics,
  registerSuperProps,
  track,
  EVENTS,
} from 'lib/analytics/analytics';
import { getUserFromToken } from 'lib/auth/authUtils';
import { hasBhoonidhiRole } from 'features/explore/bhoonidhiAccess';

// Fresh id per login → scopes a "flow" to one visit. Cleared on logout via
// resetAnalytics() (which drops registered super-properties too).
const newSessionId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * Boots analytics and identifies the user (pseudonymously) once auth settles.
 * Scope is intentionally BHOONIDHI USERS ONLY — analytics never initialises for
 * anyone else, so no data is collected on normal users. Renders nothing, and is
 * a no-op entirely until REACT_APP_POSTHOG_KEY is set.
 */
const AnalyticsProvider = () => {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isAuthenticated) {
      resetAnalytics();
      return;
    }
    if (!hasBhoonidhiRole()) return; // scoped to Bhoonidhi users only
    initAnalytics().then(() => {
      identifyUser(getUserFromToken()?.sub, { is_bhoonidhi: true, role: 'bhoonidhi' });
      // session_id → per-visit flow scope; environment → isolates dev/beta/prod
      // in the shared PostHog project (filter/breakdown by `environment`).
      registerSuperProps({
        session_id: newSessionId(),
        environment: process.env.REACT_APP_ENVIRONMENT || 'unknown',
      });
      track(EVENTS.EXPLORE_OPENED); // session-start signal, fired after init
    });
  }, [auth.isAuthenticated]);

  return null;
};

export default AnalyticsProvider;
